/**
 * StationDetails copy strings (spec §9). The web package has no
 * localisation catalogue yet (i18n deferred — see FacilitiesRow), so
 * the en strings live here keyed by the same names iOS/Android use in
 * their `Localizable` catalogues. Keeping them in one module keeps the
 * cross-platform contract auditable and the strings out of JSX literals.
 *
 * Keys map 1:1 to spec §9. `LiveStatus` is intentionally absent (F3 —
 * live status removed). `Error.*` / `Offline.Banner` are the new keys.
 */
/**
 * The full copy set for StationDetails. The component takes an optional
 * `copy` prop of this shape and threads it to every row; it defaults to
 * the English {@link StationDetailsCopy} so a provider-less render is
 * unchanged. The metro-frontend host builds a localised set from the
 * `station-details` catalog (`buildStationDetailsCopy(t)`).
 */
export interface StationDetailsCopySet {
  readonly Transfers: string
  readonly ExitsAndMap: string
  readonly Facilities: string
  readonly FirstAndLastTrain: string
  readonly IsFavorite: string
  readonly IsNotFavorite: string
  readonly ReportIssue: string
  /** SD/StationDetailsLink label — the station's own page. */
  readonly StationPageLink: string
  readonly TimetableUpsellBannerTitle: string
  readonly TimetableUpsellBannerDescription: string
  readonly TimetableUpsellBannerButton: string
  readonly ErrorTitle: string
  readonly ErrorSubtitle: string
  readonly ErrorRetry: string
  readonly OfflineBanner: string
  /** Zone badge trailing label ("zone"). */
  readonly ZoneLabel: string
  /** Header zone-badge accessible label template — `Zone {{n}}`. */
  readonly ZoneAria: (badge: string) => string
  /** Skeleton/loading overlay accessible label. */
  readonly LoadingAria: string
  /** Timetable weekday column header ("Mon–Fri"). */
  readonly TimetableWeekdays: string
  /** Timetable Saturday column header ("Sat"). */
  readonly TimetableSaturdays: string
  /** Timetable Sunday column header ("Sun"). */
  readonly TimetableSundays: string
  /** Timetable weekday column accessible label. */
  readonly TimetableWeekdaysAria: string
  /** Timetable Saturday column accessible label. */
  readonly TimetableSaturdaysAria: string
  /** Timetable Sunday column accessible label. */
  readonly TimetableSundaysAria: string
  /** Timetable "First" train row label. */
  readonly TimetableFirst: string
  /** Timetable "Last" train row label. */
  readonly TimetableLast: string
}

export const StationDetailsCopy: StationDetailsCopySet = {
  Transfers: 'Transfers',
  ExitsAndMap: 'Exits and map',
  Facilities: 'Facilities',
  FirstAndLastTrain: 'First and last train',
  IsFavorite: 'Favourite',
  IsNotFavorite: 'Add to favourites',
  ReportIssue: 'Report an issue',
  StationPageLink: 'Station details',
  TimetableUpsellBannerTitle: 'Unlock First & Last trains',
  TimetableUpsellBannerDescription: 'See first and last trains in real time for every station',
  TimetableUpsellBannerButton: 'Get Premium',
  ErrorTitle: "We couldn't load this station",
  ErrorSubtitle: 'Please try again.',
  ErrorRetry: 'Retry',
  OfflineBanner: 'No connection — showing offline results',
  ZoneLabel: 'zone',
  ZoneAria: (badge: string) => `Zone ${badge}`,
  LoadingAria: 'Loading station',
  TimetableWeekdays: 'Mon–Fri',
  TimetableSaturdays: 'Sat',
  TimetableSundays: 'Sun',
  TimetableWeekdaysAria: 'Weekdays — Monday to Friday',
  TimetableSaturdaysAria: 'Saturdays',
  TimetableSundaysAria: 'Sundays',
  TimetableFirst: 'First',
  TimetableLast: 'Last',
}

/**
 * Timetable column headers — kept for backward compatibility.
 * @deprecated Use {@link StationDetailsCopy} timetable fields.
 */
export const TIMETABLE_COLUMN_HEADERS = {
  weekdays: StationDetailsCopy.TimetableWeekdays,
  saturdays: StationDetailsCopy.TimetableSaturdays,
  sundays: StationDetailsCopy.TimetableSundays,
} as const
